"use client";
import { FC, HTMLProps, forwardRef, useEffect, useRef, useState } from "react";

// libs
import classNames from "classnames";
import sanitize from "dompurify";
import { useTranslations } from "next-intl";

// components
import { Error, ShowWhen } from "@/components/common";

// assets
import { PrimaryUnchecked, SecondaryChecked } from "@/components/assets/icons";

type DetailedRadioButtonProps = {
  id: string | number;
  label?: string;
  name: string;
  error?: string | boolean;
  value?: string | number;
  required?: boolean;
  className?: string;
  wrapperClassName?: string;
  containerClassName?: string;
  labelClassName?: string;
  disabled?: boolean;
  success?: boolean;
  icon?: any;
  infoContent?: any;
  iconPosition?: "left" | "right";
  iconBgColor?: string;
  iconColor?: string;
  activeColor?: string;
  additionalLabel?: string;
  handleTouch?: () => void;
  translation?: boolean;
} & HTMLProps<HTMLInputElement>;

const DetailedRadioButton: FC<DetailedRadioButtonProps> = forwardRef<
  HTMLInputElement,
  DetailedRadioButtonProps
>(
  (
    {
      id,
      label = "",
      value,
      name,
      maxLength,
      className,
      wrapperClassName,
      labelClassName,
      containerClassName,
      disabled,
      error,
      success,
      icon,
      infoContent,
      iconPosition = "right",
      iconBgColor,
      iconColor,
      activeColor,
      additionalLabel,
      width = "w-full",
      children,
      handleTouch,
      translation = true,
      ...props
    },
    ref,
  ) => {
    const t = useTranslations();
    const inputClasses = classNames(
      "w-[30px]",
      "h-[30px]",
      "px-8",
      "border",
      "text-base",
      "focus:outline-none",
      className,
      {
        // error
        "border-red-600": !!error,
        // active
        "border-transparent active:border-[#cfe0e2]": !error,
        // disabled
        "disabled:bg-zinc-100 disabled:text-zinc-400 disabled:border-none":
          !error,
        // success
        "border-emerald-600": success,
      },
    );

    const wrapperRef = useRef(null);
    const [touched, setTouched] = useState(false);

    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        if (touched) {
          handleTouch();
        }
      }
    };
    useEffect(() => {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [touched]);

    return (
      <div
        ref={wrapperRef}
        className={classNames(
          "relative border-[2px] p-[6px]",
          error
            ? "border-red-600"
            : props.checked || props.defaultChecked
              ? activeColor || "border-[#cbf3ba]"
              : "border-[#f9f9f9]",
          (props.checked || props.defaultChecked) && !!children
            ? "rounded-[21px]"
            : "rounded-[35px]",
          containerClassName,
          width,
        )}
      >
        <div
          className={classNames(
            "input-wrapper relative w-full bg-transparent py-[1px]",
            wrapperClassName,
          )}
        >
          <label
            className={classNames(
              "flex cursor-pointer items-center justify-start text-xs font-bold text-black",
              labelClassName,
            )}
            htmlFor={id}
          >
            {icon && (
              <div
                className={classNames(
                  "mr-[10px] flex h-[46px] min-h-[46px] w-[46px] min-w-[46px] items-center justify-center rounded-[50%]",
                  iconBgColor || "bg-[#F9F9F9]",
                  iconColor && "detailed-radio-icon-wrapper",
                  iconColor,
                )}
              >
                {icon}
              </div>
            )}
            <div>
              <span
                className="block text-sm font-semibold text-black"
                dangerouslySetInnerHTML={{
                  __html: sanitize.sanitize(translation ? t(label) : label),
                }}
              />
              {additionalLabel && (
                <span
                  className="mt-[4px] text-sm font-normal text-black opacity-60"
                  dangerouslySetInnerHTML={{
                    __html: sanitize.sanitize(
                      translation ? t(additionalLabel) : additionalLabel,
                    ),
                  }}
                />
              )}
            </div>
            <div className="relative ml-auto flex">
              <input
                id={id}
                ref={ref}
                className={classNames(
                  "relative z-10 cursor-pointer pt-4 opacity-0",
                  inputClasses,
                )}
                name={name}
                disabled={disabled}
                maxLength={maxLength}
                type="radio"
                placeholder=""
                value={value || ""}
                checked={props.checked}
                {...props}
              />
              {props.checked || props.defaultChecked ? (
                <div
                  className={classNames(
                    "absolute left-0 top-0 z-0 flex h-[30px] w-[30px] items-center justify-center fill-red-600",
                    !!error && "detailed-radio-icon-wrapper",
                  )}
                >
                  <SecondaryChecked />
                </div>
              ) : (
                <div
                  className={classNames(
                    "absolute left-0 top-0 z-0 flex h-[30px] w-[30px] items-center justify-center fill-red-600",
                    !!error && "detailed-radio-icon-wrapper",
                  )}
                >
                  <PrimaryUnchecked />
                </div>
              )}
            </div>
          </label>
        </div>
        <ShowWhen condition={!!children}>
          <div
            className={classNames(
              props.checked || props.defaultChecked
                ? "opacity-1 h-fit px-[2px] pt-[20px]"
                : "h-0 opacity-0",
              "duration-400 transition-all",
            )}
          >
            {children}
          </div>
        </ShowWhen>
        {error && typeof error === "string" && <Error text={error} />}
      </div>
    );
  },
);

DetailedRadioButton.displayName = "DetailedRadioButton";

export { DetailedRadioButton };
